import { observable } from "@legendapp/state";
import { getArrivalReport } from "@/apis/clients/arrivalClient";
import {
  createRequestGuard,
  reportScopeKey,
  toErrorMessage,
} from "@/stores/storeHelpers";
import type { ArrivalReportRow, ReportScope } from "@/types/reporting";

interface ArrivalState {
  rows: ArrivalReportRow[];
  isLoading: boolean;
  error: string | null;
  loadedForScopeKey: string | null;
}

const defaultValues: ArrivalState = {
  rows: [],
  isLoading: true,
  error: null,
  loadedForScopeKey: null,
};

export const ArrivalStore$ = observable(defaultValues);

const guard = createRequestGuard();

export const fetchArrivalReport = async (scope: ReportScope) => {
  const scopeKey = reportScopeKey(scope);
  const result = guard.check(scopeKey, ArrivalStore$.loadedForScopeKey.get());
  if (result.outcome === "cached") {
    ArrivalStore$.assign({ isLoading: false, error: null });
    return;
  }
  if (result.outcome === "in-flight") return;
  const { requestId } = result;
  ArrivalStore$.assign({ isLoading: true, error: null });

  try {
    const response = await getArrivalReport(scope);
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    ArrivalStore$.assign({
      rows: response.results,
      loadedForScopeKey: scopeKey,
    });
  } catch (error) {
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    ArrivalStore$.error.set(toErrorMessage(error));
  } finally {
    if (!guard.isStale(requestId)) {
      ArrivalStore$.isLoading.set(false);
    }
  }
};

export const clearArrivalReport = () => {
  guard.invalidate();
  ArrivalStore$.assign({
    rows: [],
    isLoading: false,
    loadedForScopeKey: null,
    error: null,
  });
};
